import { ChangeDetectionStrategy, Component, Injectable, NgZone, inject } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { DOCUMENT } from '@angular/common';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { TranslationService } from '../../../core/services/i18n/translation.service';
import { AppDialogService, PHONE } from '../ui/dialog/app-dialog.service';

export interface SelectSheetData {
  title: string;
  options: { label: string; disabled: boolean }[];
  selected: number;
}

/** The options of a native select as full-width rows in a bottom sheet; closes with the tapped row's index */
@Component({
  selector: 'app-select-sheet',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="flex max-h-[70vh] flex-col rounded-t-2xl bg-white pb-[env(safe-area-inset-bottom)] shadow-xl md:rounded-lg"
    >
      <h2 class="truncate border-b border-gray-200 px-4 py-3 text-base font-semibold text-gray-900">
        {{ data.title }}
      </h2>
      <div role="listbox" [attr.aria-label]="data.title" class="overflow-y-auto overscroll-contain">
        @for (option of data.options; track $index) {
          <button
            type="button"
            role="option"
            [attr.aria-selected]="$index === data.selected"
            [attr.aria-disabled]="option.disabled"
            [attr.cdkFocusInitial]="$index === data.selected ? '' : null"
            class="flex min-h-12 w-full items-center gap-3 border-b border-gray-100 px-4 py-2 text-left text-base text-gray-900 last:border-b-0 touch-manipulation hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent aria-disabled:cursor-not-allowed aria-disabled:text-gray-400 aria-disabled:hover:bg-white"
            (click)="pick($index)"
          >
            <span class="min-w-0 flex-1 break-words">{{ option.label }}</span>
            @if ($index === data.selected) {
              <i class="fas fa-check text-accent-600" aria-hidden="true"></i>
            }
          </button>
        }
      </div>
      <button
        type="button"
        class="m-3 h-11 touch-manipulation rounded-md border border-gray-300 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        (click)="ref.close(undefined)"
      >
        {{ i18n.t('common.cancel') }}
      </button>
    </div>
  `,
})
export class SelectSheetComponent {
  protected readonly i18n = inject(TranslationService);
  protected readonly data = inject<SelectSheetData>(DIALOG_DATA);
  protected readonly ref = inject<DialogRef<number | undefined>>(DialogRef);

  protected pick(index: number): void {
    if (!this.data.options[index].disabled) this.ref.close(index);
  }
}

/**
 * Card 924: on a phone every single native select opens SelectSheetComponent instead of the browser's
 * list (ui-standard NATIVE: bottom sheets), app-wide from one listener so the 175 selects need no edit.
 * The choice is written back to the select with input and change events, so ngModel and reactive forms
 * see it as a normal pick. Desktop, multiple and data-native-select selects stay native, and so does a
 * keyboard opening (a phone with a keyboard keeps the browser's list).
 */
@Injectable({ providedIn: 'root' })
export class SelectSheetService {
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);
  private readonly breakpoints = inject(BreakpointObserver);
  private readonly dialogs = inject(AppDialogService);
  private readonly i18n = inject(TranslationService);
  private open = false;
  private touchStart: { x: number; y: number } | null = null;

  private readonly onTouchStart = (event: TouchEvent) => {
    const touch = event.touches[0];
    this.touchStart = touch ? { x: touch.clientX, y: touch.clientY } : null;
  };

  // A finger that moved was a scroll over the select: neither the sheet nor the native list opens
  private readonly onTouchEnd = (event: TouchEvent) => {
    const touch = event.changedTouches[0];
    const start = this.touchStart;
    if (start && touch && Math.hypot(touch.clientX - start.x, touch.clientY - start.y) > 10) return;
    this.intercept(event);
  };

  private readonly onMouseDown = (event: MouseEvent) => {
    if (event.button === 0) this.intercept(event);
  };

  // Outside the zone: a touch or click anywhere must not run change detection; only opening the sheet does
  start(): void {
    this.zone.runOutsideAngular(() => {
      this.document.addEventListener('touchstart', this.onTouchStart, {
        capture: true,
        passive: true,
      });
      this.document.addEventListener('touchend', this.onTouchEnd, true);
      this.document.addEventListener('mousedown', this.onMouseDown, true);
    });
  }

  stop(): void {
    this.document.removeEventListener('touchstart', this.onTouchStart, true);
    this.document.removeEventListener('touchend', this.onTouchEnd, true);
    this.document.removeEventListener('mousedown', this.onMouseDown, true);
  }

  private intercept(event: Event): void {
    if (!this.breakpoints.isMatched(PHONE)) return;
    const target = event.target as Element | null;
    // A tap on a select's label opens it too
    const select = target?.closest?.('select') ?? target?.closest?.('label')?.control;
    if (
      !(select instanceof HTMLSelectElement) ||
      select.multiple ||
      select.size > 1 ||
      select.matches(':disabled') ||
      select.hasAttribute('data-native-select')
    ) {
      return;
    }
    event.preventDefault();
    if (!this.open) this.zone.run(() => this.show(select));
  }

  private show(select: HTMLSelectElement): void {
    const title = this.title(select);
    // The options as they are now: the pick is written back to the same option, wherever it sits by then
    const options = Array.from(select.options);
    const ref = this.dialogs.openSheet<number | undefined, SelectSheetData>(SelectSheetComponent, {
      ariaLabel: title,
      data: {
        title,
        options: options.map((o) => ({ label: o.label, disabled: o.matches(':disabled') })),
        selected: select.selectedIndex,
      },
      // The select never took focus (its tap was stopped), and focusing it on iOS opens the native list
      restoreFocus: false,
    });
    this.open = true;
    ref.closed.subscribe((index) => {
      this.open = false;
      const picked = index == null ? null : options[index];
      if (picked && !picked.selected && select.contains(picked)) {
        picked.selected = true;
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      // Touched for the forms, as a native pick that ends in a blur
      select.dispatchEvent(new FocusEvent('blur'));
    });
  }

  /** The select's own label, as a screen reader would name it */
  private title(select: HTMLSelectElement): string {
    const labelledBy = select.getAttribute('aria-labelledby');
    const named =
      (select.labels?.[0] && labelText(select.labels[0])) ||
      select.getAttribute('aria-label') ||
      (labelledBy && this.document.getElementById(labelledBy)?.textContent) ||
      select.title ||
      looseLabel(select);
    return named?.replace(/\s+/g, ' ').trim() || this.i18n.t('selectSheet.choose');
  }
}

/** The label right before the select that names no field (no for): the field name the page shows above it.
 *  Many filters are a <label> then a <select> with no for/id (card 1067), and the sheet said "Elegir". Only the
 *  sibling right before counts: a label further back names another field (review 2 Nov). */
function looseLabel(select: HTMLSelectElement): string {
  const el = select.previousElementSibling;
  return el instanceof HTMLLabelElement && !el.htmlFor ? labelText(el) : '';
}

/** A label's own words, without the options of a select nested inside it */
function labelText(label: HTMLLabelElement): string {
  const copy = label.cloneNode(true) as HTMLLabelElement;
  copy.querySelectorAll('select').forEach((nested) => nested.remove());
  return copy.textContent ?? '';
}
